import React, { useState, useEffect } from 'react';
import TimeSeriesChart from '../components/TimeSeriesChart';
import { fetchAverageUserActivity } from '../services/api';
import Header from '../components/Header';
import FilterDropdown from '../components/FilterDropdown';
import { createSafeDateFromString } from '../utils/dateUtils';

const PercentageEngagement = () => {
    const [chartData, setChartData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedMonths, setSelectedMonths] = useState(new Set());
    const [availableMonths, setAvailableMonths] = useState({});

    useEffect(() => {
        const loadData = async () => {
            try {
                setLoading(true);
                const rawData = await fetchAverageUserActivity();
                
                // Calculate aggmonth field and filter data from January 2017 onwards
                const processedData = rawData
                    .filter(item => item.month !== null) // Remove null months
                    .map(item => {
                        // Calculate aggmonth: Parse the month string (YYYY-MM) to a Date object
                        const aggmonth = createSafeDateFromString(item.month + '-01'); // Add day to make valid date
                        return {
                            ...item,
                            aggmonth
                        };
                    })
                    .filter(item => {
                        // Filter: cohortmonthavg2017+ - only show data from January 2017 onwards
                        const jan2017 = createSafeDateFromString('2017-01-01');
                        return item.aggmonth >= jan2017;
                    })
                    .sort((a, b) => a.aggmonth - b.aggmonth); // Sort chronologically

                // Create available months object for FilterDropdown
                const monthsObj = {};
                processedData.forEach(item => {
                    monthsObj[item.month] = 1; // Count is not meaningful for months, just use 1
                });
                setAvailableMonths(monthsObj);

                // Initialize selected months to all available months
                const allMonths = new Set(Object.keys(monthsObj));
                setSelectedMonths(allMonths);

                // Store the processed data for filtering
                setChartData(processedData);
            } catch (err) {
                console.error('Error loading average user activity data:', err);
                setError('Failed to load data');
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, []);

    // Transform data for TimeSeriesChart based on selected months
    const getTransformedChartData = () => {
        if (!chartData || chartData.length === 0) return [];

        // Filter data based on selected months
        const filteredData = chartData.filter(item => selectedMonths.has(item.month));

        return [
            {
                id: 'Average % of Users Writing',
                data: filteredData.map(item => ({
                    x: item.aggmonth,
                    y: item.one_piece * 100 // Convert to percentage
                }))
            },
            {
                id: 'Average % of Users Commenting',
                data: filteredData.map(item => ({
                    x: item.aggmonth,
                    y: item.one_comment * 100 // Convert to percentage
                }))
            },
            {
                id: 'Average % of Users Reviewing',
                data: filteredData.map(item => ({
                    x: item.aggmonth,
                    y: item.one_review * 100 // Convert to percentage
                }))
            }
        ];
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="text-lg text-gray-600">Loading percentage engagement data...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="text-lg text-red-600">Error: {error}</div>
            </div>
        );
    }

    return (
        <div>
            <Header pageTitle="Percentage Engagement" />
            
            {/* Filter Controls */}
            <div className="mt-6 mx-6 flex justify-start">
                <FilterDropdown
                    label="Filter by Month"
                    items={availableMonths}
                    selectedItems={selectedMonths}
                    onSelectionChange={setSelectedMonths}
                    showCount={false}
                    formatLabel={(month) => month}
                    sortItems={(itemKeys) => itemKeys.sort().reverse()} // Sort newest to oldest
                />
            </div>
            
            <TimeSeriesChart
                data={getTransformedChartData()}
                title=""
                xAxisLabel="Time"
                yAxisLabel="Average % of Users"
                height={500}
                showLegend={true}
                legendPosition="top-right"
                dateType="monthly"
                margin={{ top: 60, right: 120, bottom: 60, left: 80 }}
                xAxisLegendOffset={80}
                yAxisLegendOffset={-50}
            />
        </div>
    );
};

export default PercentageEngagement;